import axios from 'axios';
import { getApiUrl } from '../config/environment';

const api = axios.create({
  baseURL: `${getApiUrl()}/financial`,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor para adicionar token de autenticação
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interfaces
export interface FinancialProject {
  id: string;
  name: string;
  description?: string;
  status: 'active' | 'paused' | 'completed' | 'archived';
  start_date?: string;
  end_date?: string;
  currency: string;
  created_at: string;
  updated_at: string;
}

export interface RevenueEntry {
  id: string;
  project_id: string;
  description: string;
  amount: number;
  transaction_date: string;
  category: 'product_sales' | 'upsell' | 'affiliate' | 'other';
  payment_method?: string;
  platform?: string;
  platform_fee_percentage?: number;
  platform_fee_amount: number;
  net_amount: number;
  contact_id?: string;
  webhook_log_id?: string;
  notes?: string;
  metadata?: any;
  created_at: string;
  updated_at: string;
}

export interface ExpenseCategory {
  id: string;
  name: string;
  description?: string;
  color: string;
  icon?: string;
  is_active: boolean;
}

export interface ExpenseEntry {
  id: string;
  project_id: string;
  category_id?: string;
  description: string;
  amount: number;
  expense_date: string;
  payment_method?: string;
  is_recurring: boolean;
  recurring_frequency?: string;
  related_campaign_id?: number;
  related_ad_account_id?: number;
  receipt_url?: string;
  notes?: string;
  metadata?: any;
  expense_categories?: ExpenseCategory;
  created_at: string;
  updated_at: string;
}

export interface FinancialDashboard {
  summary: {
    project_id?: string;
    project_name?: string;
    total_revenue?: number;
    total_platform_fees?: number;
    total_net_revenue?: number;
    total_expenses?: number;
    net_profit?: number;
    profit_margin_percentage?: number;
    total_tax?: number;
    total_pending?: number;
    total_refunded?: number;
    total_chargeback?: number;
    total_sales?: number;
  } | null;
  metrics: {
    total_revenue: number;
    total_expenses: number;
    total_platform_fees: number;
    net_profit: number;
    profit_margin_percentage: number;
    faturamento_liquido?: number;
    gastos_anuncios?: number;
    roas?: number;
    lucro?: number;
    vendas_pendentes?: number;
    roi?: number;
    vendas_reembolsadas?: number;
    margem_lucro?: number;
    imposto?: number;
    reembolso_percent?: number;
    chargeback_percent?: number;
    arpu?: number;
    total_vendas?: number;
  };
  revenue_by_date: Array<{
    transaction_date: string;
    net_amount: number;
    amount: number;
    platform_fee_amount: number;
  }>;
  expenses_by_date: Array<{
    expense_date: string;
    amount: number;
  }>;
  expenses_by_category: Array<{
    category_id: string;
    category_name: string;
    category_color: string;
    total_amount: number;
    transaction_count: number;
  }>;
  revenue_by_category: Array<{
    category: string;
    total_amount: number;
    total_fees: number;
    total_net_amount: number;
    transaction_count: number;
  }>;
  revenue_by_payment_method?: Array<{
    payment_method: string;
    count: number;
    amount: number;
  }>;
}

class FinancialControlService {
  // ========== PROJETOS ==========

  static async listProjects(): Promise<{ success: boolean; data?: FinancialProject[]; error?: string }> {
    try {
      const response = await api.get('/projects');
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Erro ao listar projetos',
      };
    }
  }

  static async createProject(project: {
    name: string;
    description?: string;
    status?: string;
    start_date?: string;
    end_date?: string;
    currency?: string;
  }): Promise<{ success: boolean; data?: FinancialProject; error?: string }> {
    try {
      const response = await api.post('/projects', project);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Erro ao criar projeto',
      };
    }
  }

  static async updateProject(id: string, project: Partial<FinancialProject>): Promise<{ success: boolean; data?: FinancialProject; error?: string }> {
    try {
      const response = await api.put(`/projects/${id}`, project);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Erro ao atualizar projeto',
      };
    }
  }

  static async deleteProject(id: string): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await api.delete(`/projects/${id}`);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Erro ao deletar projeto',
      };
    }
  }

  // ========== RECEITAS ==========

  static async listRevenue(params?: {
    project_id?: string;
    start_date?: string;
    end_date?: string;
    category?: string;
  }): Promise<{ success: boolean; data?: RevenueEntry[]; error?: string }> {
    try {
      const response = await api.get('/revenue', { params });
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Erro ao listar receitas',
      };
    }
  }

  static async createRevenue(revenue: {
    project_id: string;
    description: string;
    amount: number;
    transaction_date: string;
    category?: string;
    payment_method?: string;
    platform?: string;
    platform_fee_percentage?: number;
    platform_fee_amount?: number;
    notes?: string;
    metadata?: any;
  }): Promise<{ success: boolean; data?: RevenueEntry; error?: string }> {
    try {
      const response = await api.post('/revenue', revenue);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Erro ao criar receita',
      };
    }
  }

  static async updateRevenue(id: string, revenue: Partial<RevenueEntry>): Promise<{ success: boolean; data?: RevenueEntry; error?: string }> {
    try {
      const response = await api.put(`/revenue/${id}`, revenue);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Erro ao atualizar receita',
      };
    }
  }

  static async deleteRevenue(id: string): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await api.delete(`/revenue/${id}`);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Erro ao deletar receita',
      };
    }
  }

  // ========== DESPESAS ==========

  static async listExpenses(params?: {
    project_id?: string;
    start_date?: string;
    end_date?: string;
    category_id?: string;
  }): Promise<{ success: boolean; data?: ExpenseEntry[]; error?: string }> {
    try {
      const response = await api.get('/expenses', { params });
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Erro ao listar despesas',
      };
    }
  }

  static async createExpense(expense: {
    project_id: string;
    category_id?: string;
    description: string;
    amount: number;
    expense_date: string;
    payment_method?: string;
    is_recurring?: boolean;
    recurring_frequency?: string;
    receipt_url?: string;
    notes?: string;
    metadata?: any;
  }): Promise<{ success: boolean; data?: ExpenseEntry; error?: string }> {
    try {
      const response = await api.post('/expenses', expense);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Erro ao criar despesa',
      };
    }
  }

  static async updateExpense(id: string, expense: Partial<ExpenseEntry>): Promise<{ success: boolean; data?: ExpenseEntry; error?: string }> {
    try {
      const response = await api.put(`/expenses/${id}`, expense);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Erro ao atualizar despesa',
      };
    }
  }

  static async deleteExpense(id: string): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await api.delete(`/expenses/${id}`);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Erro ao deletar despesa',
      };
    }
  }

  // ========== CATEGORIAS ==========

  static async listExpenseCategories(): Promise<{ success: boolean; data?: ExpenseCategory[]; error?: string }> {
    try {
      const response = await api.get('/expense-categories');
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Erro ao listar categorias',
      };
    }
  }

  // ========== DASHBOARD ==========

  static async getDashboard(params?: {
    project_id?: string;
    start_date?: string;
    end_date?: string;
  }): Promise<{ success: boolean; data?: FinancialDashboard; error?: string }> {
    try {
      const response = await api.get('/dashboard', { params });
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Erro ao buscar dashboard',
      };
    }
  }

  // ========== SINCRONIZAÇÃO ==========

  static async syncFacebookAds(params: {
    project_id: string;
    category_id?: string;
    start_date?: string;
    end_date?: string;
  }): Promise<{ success: boolean; data?: { synced_count: number }; error?: string }> {
    try {
      const response = await api.post('/sync-facebook-ads', params);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Erro ao sincronizar Facebook Ads',
      };
    }
  }
}

export default FinancialControlService;

